import React, { useEffect, useState, memo, createElement } from 'react';
import { motion } from 'framer-motion';
import { MoonIcon, AlertTriangleIcon, XCircleIcon, CpuIcon, BookOpenIcon, FileTextIcon, HomeIcon, WindIcon, ClipboardListIcon, CheckCircleIcon, ShieldIcon, ZapIcon, ChevronDownIcon, XIcon } from 'lucide-react';
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 40
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  transition: {
    duration: 0.6,
    ease: 'easeOut'
  },
  viewport: {
    once: true,
    margin: '-100px'
  }
};
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
    _fbq: (...args: any[]) => void;
  }
}
function initMetaPixel() {
  if (window.fbq) return;
  const n: any = window.fbq = function () {
    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
  };
  if (!window._fbq) window._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];
  const t = document.createElement('script');
  t.async = true;
  t.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(t);
  window.fbq('init', '1743314353311745');
  window.fbq('track', 'PageView');
}
export function LandingPage() {
  useEffect(() => {
    initMetaPixel();
  }, []);
  return <div className="w-full min-h-screen bg-[#0a0e1a] text-gray-100" data-id="element-1">
      <HeroSection data-id="element-2" />
      <OpeningHook data-id="element-3" />
      <ProblemSection data-id="element-4" />
      <WhyItDidntWork data-id="element-5" />
      <UniqueMechanism data-id="element-6" />
      <WhatYouGet data-id="element-7" />
      <WhoThisIsFor data-id="element-8" />
      <HowItWorks data-id="element-9" />
      <ProofSection data-id="element-10" />
      <FAQSection data-id="element-11" />
      <OfferSection data-id="element-12" />
      <Footer data-id="element-13" />
    </div>;
}
function HeroSection() {
  return <section className="relative min-h-screen flex items-center justify-center px-6 py-20 overflow-hidden" data-id="element-14">
      {/* Subtle star background effect */}
      <div className="absolute inset-0 opacity-30" data-id="element-15">
        {[...Array(50)].map((_, i) => <div key={i} className="absolute w-1 h-1 bg-white rounded-full animate-pulse" style={{
        top: `${Math.random() * 100}%`,
        left: `${Math.random() * 100}%`,
        animationDelay: `${Math.random() * 3}s`,
        animationDuration: `${2 + Math.random() * 3}s`
      }} data-id="element-16" />)}
      </div>

      <div className="relative z-10 max-w-5xl mx-auto text-center" data-id="element-17">
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8
      }} className="mb-12" data-id="element-18">
          <img src="https://cdn.magicpatterns.com/uploads/wU6DVKcMEgSHEEDcS6gKxQ/heylloyd-image-1776304995495.png" alt="Apaga Tu Mente en 15 Minutos" className="w-full max-w-md mx-auto drop-shadow-2xl" style={{
          filter: 'drop-shadow(0 0 40px rgba(245, 158, 11, 0.3))'
        }} data-id="element-19" />
        </motion.div>

        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="font-serif text-5xl md:text-7xl mb-6 text-white" data-id="element-20">
          Apaga Tu Mente en 15 Minutos
        </motion.h1>

        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.4
      }} className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed" data-id="element-21">
          El sistema de "Vaciado de RAM Humana" para silenciar el ruido mental,
          eliminar la ansiedad nocturna y dormir profundamente hoy mismo.
        </motion.h2>

        <motion.button initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.6
      }} whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} onClick={() => document.getElementById('offer')?.scrollIntoView({
        behavior: 'smooth'
      })} className="bg-amber-500 hover:bg-amber-600 text-gray-900 font-bold text-lg px-12 py-5 rounded-lg shadow-2xl transition-all cursor-pointer" data-id="element-22">
          ¡QUIERO DORMIR BIEN HOY MISMO!
        </motion.button>
      </div>
    </section>;
}
function OpeningHook() {
  return <motion.section {...fadeInUp} className="py-20 px-6 bg-[#111827]" data-id="element-23">
      <div className="max-w-3xl mx-auto" data-id="element-24">
        <div className="flex items-start gap-4 mb-8" data-id="element-25">
          <MoonIcon className="w-12 h-12 text-amber-500 flex-shrink-0 mt-1" data-id="element-26" />
          <div data-id="element-27">
            <h3 className="font-serif text-3xl md:text-4xl mb-6 text-white" data-id="element-28">
              Son las 2:00 AM
            </h3>
            <div className="text-gray-300 text-lg leading-relaxed space-y-4 italic" data-id="element-29">
              <p data-id="element-30">
                Estás agotado. Tu cuerpo pesa como el plomo, pero tus ojos están
                abiertos de par en par. Tu mente está repasando esa conversación
                de ayer, planificando la lista de tareas de mañana o
                preocupándose por cosas que no puedes resolver ahora.
              </p>
              <p data-id="element-31">
                Intentas cerrar los ojos, pero tu cerebro se siente "eléctrico".
                Cuanto más intentas dormir, más despierto te sientes. No es que
                te falte sueño, es que tu cerebro ha olvidado cómo apagar el
                interruptor.
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.section>;
}
function ProblemSection() {
  return <motion.section {...fadeInUp} className="py-20 px-6 bg-[#0a0e1a]" data-id="element-32">
      <div className="max-w-3xl mx-auto" data-id="element-33">
        <div className="flex items-start gap-4 mb-8" data-id="element-34">
          <AlertTriangleIcon className="w-12 h-12 text-red-500 flex-shrink-0 mt-1" data-id="element-35" />
          <div data-id="element-36">
            <h3 className="font-serif text-3xl md:text-4xl mb-6 text-white" data-id="element-37">
              El insomnio por ansiedad no es una enfermedad, es un error de
              software.
            </h3>
            <div className="text-gray-300 text-lg leading-relaxed space-y-4" data-id="element-38">
              <p data-id="element-39">
                Durante el día, acumulas cientos de "procesos abiertos" en tu
                mente. Si no los cierras correctamente, tu sistema nervioso
                permanece en modo de supervivencia.
              </p>
              <p data-id="element-40">
                Tu cerebro cree que todavía estás en la oficina o en medio de
                una crisis. Por eso, aunque tomes melatonina o té, la "corriente
                eléctrica" en tu frente no desaparece. Estás operando con la
                memoria RAM de tu mente saturada, y eso te está destruyendo la
                salud, el humor y tu capacidad de ganar dinero.
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.section>;
}
function WhyItDidntWork() {
  const items = [{
    title: 'Las pastillas y suplementos',
    description: 'Atacan el síntoma químico, pero no el proceso de pensamiento rumiante que dispara el cortisol.'
  }, {
    title: 'Contar ovejas',
    description: 'Es un esfuerzo cognitivo activo que mantiene al cerebro trabajando en lugar de relajarse.'
  }, {
    title: 'Música de relajación',
    description: 'Se convierte en ruido de fondo mientras sigues pensando en tus problemas reales.'
  }, {
    title: 'Intentar "dejar la mente en blanco"',
    description: 'Es imposible. Intentar no pensar genera más pensamientos y más frustración.'
  }];
  return <motion.section {...fadeInUp} className="py-20 px-6 bg-[#111827]" data-id="element-41">
      <div className="max-w-4xl mx-auto" data-id="element-42">
        <h3 className="font-serif text-3xl md:text-4xl mb-12 text-center text-white" data-id="element-43">
          Por qué lo que intentaste no funcionó
        </h3>
        <div className="space-y-6" data-id="element-44">
          {items.map((item, index) => <motion.div key={index} initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} viewport={{
          once: true
        }} className="flex items-start gap-4 bg-[#0a0e1a] p-6 rounded-lg border border-gray-800" data-id="element-45">
              <XCircleIcon className="w-8 h-8 text-red-500 flex-shrink-0 mt-1" data-id="element-46" />
              <div data-id="element-47">
                <h4 className="text-xl font-semibold mb-2 text-white" data-id="element-48">
                  {item.title}
                </h4>
                <p className="text-gray-400" data-id="element-49">{item.description}</p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </motion.section>;
}
function UniqueMechanism() {
  return <motion.section {...fadeInUp} className="py-20 px-6 bg-[#0a0e1a]" data-id="element-50">
      <div className="max-w-3xl mx-auto text-center" data-id="element-51">
        <CpuIcon className="w-16 h-16 text-amber-500 mx-auto mb-6" data-id="element-52" />
        <h3 className="font-serif text-4xl md:text-5xl mb-8 text-white" data-id="element-53">
          El Método del Vaciado de RAM Humana
        </h3>
        <p className="text-gray-300 text-lg leading-relaxed" data-id="element-54">
          En lugar de luchar contra tus pensamientos, este protocolo te enseña a
          externalizarlos de forma técnica. Al igual que una computadora
          necesita cerrar pestañas para enfriarse, tu mente necesita "descargar"
          los procesos activos en un formato analógico específico. Este método
          engaña a tu sistema nervioso para que cree que todas las tareas están
          a salvo, dándole el permiso biológico para apagarse en menos de 15
          minutos.
        </p>
      </div>
    </motion.section>;
}
function WhatYouGet() {
  const items = [{
    icon: BookOpenIcon,
    title: 'El Protocolo Central "Mente en Off"',
    description: 'El paso a paso exacto para inducir el sueño profundo.'
  }, {
    icon: FileTextIcon,
    title: 'Guía de Vaciado de RAM',
    description: 'Cómo limpiar tus procesos mentales antes de tocar la almohada.'
  }, {
    icon: HomeIcon,
    title: 'Auditoría de Habitación Express',
    description: '3 ajustes de 60 segundos para convertir tu dormitorio en un santuario de descanso.'
  }, {
    icon: WindIcon,
    title: 'Técnica de Respiración Vagal 4-7-8',
    description: 'El "hack" físico para bajar tu ritmo cardíaco al instante.'
  }, {
    icon: ClipboardListIcon,
    title: 'Hoja de Trabajo Anti-Rumiación',
    description: 'Para esos pensamientos intrusivos de las 3:00 AM.'
  }];
  return <motion.section {...fadeInUp} className="py-20 px-6 bg-[#111827]" data-id="element-55">
      <div className="max-w-6xl mx-auto" data-id="element-56">
        <h3 className="font-serif text-4xl md:text-5xl mb-12 text-center text-white" data-id="element-57">
          Qué obtienes
        </h3>
        <div className="grid md:grid-cols-2 gap-6" data-id="element-58">
          {items.map((item, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} viewport={{
          once: true
        }} className="bg-[#0a0e1a] p-8 rounded-lg border border-gray-800 hover:border-amber-500/50 transition-all" data-id="element-59">
              <item.icon className="w-12 h-12 text-amber-500 mb-4" data-id="element-60" />
              <h4 className="text-xl font-semibold mb-3 text-white" data-id="element-61">
                {item.title}
              </h4>
              <p className="text-gray-400" data-id="element-62">{item.description}</p>
            </motion.div>)}
        </div>
      </div>
    </motion.section>;
}
function WhoThisIsFor() {
  const forItems = ['Emprendedores y profesionales con alto nivel de estrés.', 'Personas que se sienten "eléctricas" o alertas al acostarse.', 'Quienes sufren de rumiación mental (no poder dejar de pensar).', 'Padres y madres agotados que necesitan maximizar sus pocas horas de descanso.'];
  const notForItems = ['Personas buscando soluciones mágicas sin aplicar el método.', 'Casos de insomnio clínico severo que requieren intervención quirúrgica o psiquiátrica.', 'Personas que no están dispuestas a dejar el teléfono 15 minutos antes de dormir.'];
  return <motion.section {...fadeInUp} className="py-20 px-6 bg-[#0a0e1a]" data-id="element-63">
      <div className="max-w-6xl mx-auto" data-id="element-64">
        <div className="grid md:grid-cols-2 gap-8" data-id="element-65">
          <div data-id="element-66">
            <h3 className="font-serif text-3xl mb-8 text-white" data-id="element-67">
              Para quién es esto
            </h3>
            <div className="space-y-4" data-id="element-68">
              {forItems.map((item, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} viewport={{
              once: true
            }} className="flex items-start gap-3" data-id="element-69">
                  <CheckCircleIcon className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" data-id="element-70" />
                  <p className="text-gray-300" data-id="element-71">{item}</p>
                </motion.div>)}
            </div>
          </div>

          <div data-id="element-72">
            <h3 className="font-serif text-3xl mb-8 text-white" data-id="element-73">
              Para quién NO es esto
            </h3>
            <div className="space-y-4" data-id="element-74">
              {notForItems.map((item, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} viewport={{
              once: true
            }} className="flex items-start gap-3" data-id="element-75">
                  <XCircleIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" data-id="element-76" />
                  <p className="text-gray-300" data-id="element-77">{item}</p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </div>
    </motion.section>;
}
function HowItWorks() {
  const steps = [{
    number: 1,
    title: 'Descarga',
    description: 'Ejecutas el vaciado de procesos mentales siguiendo la guía.'
  }, {
    number: 2,
    title: 'Desconexión',
    description: 'Aplicas el protocolo de respiración vagal en la cama.'
  }, {
    number: 3,
    title: 'Sueño',
    description: 'Tu cerebro activa el modo parasimpático de forma natural y automática.'
  }];
  return <motion.section {...fadeInUp} className="py-20 px-6 bg-[#111827]" data-id="element-78">
      <div className="max-w-5xl mx-auto" data-id="element-79">
        <h3 className="font-serif text-4xl md:text-5xl mb-16 text-center text-white" data-id="element-80">
          Cómo funciona
        </h3>
        <div className="grid md:grid-cols-3 gap-8" data-id="element-81">
          {steps.map((step, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: index * 0.2
        }} viewport={{
          once: true
        }} className="text-center" data-id="element-82">
              <div className="w-20 h-20 rounded-full bg-amber-500 text-gray-900 flex items-center justify-center text-3xl font-bold mx-auto mb-6" data-id="element-83">
                {step.number}
              </div>
              <h4 className="text-2xl font-semibold mb-4 text-white" data-id="element-84">
                {step.title}
              </h4>
              <p className="text-gray-400" data-id="element-85">{step.description}</p>
            </motion.div>)}
        </div>
      </div>
    </motion.section>;
}
function ProofSection() {
  return <motion.section {...fadeInUp} className="py-20 px-6 bg-[#0a0e1a]" data-id="element-86">
      <div className="max-w-3xl mx-auto" data-id="element-87">
        <div className="bg-[#111827] p-8 md:p-12 rounded-lg border-l-4 border-amber-500" data-id="element-88">
          <p className="text-gray-300 text-lg leading-relaxed italic" data-id="element-89">
            En la primera noche de uso, notarás que la presión en tu pecho
            disminuye y que esa sensación de "ruido eléctrico" en tu frente se
            apaga. No es magia, es neurobiología aplicada. En 7 días, tu ciclo
            de sueño se habrá regulado, despertando con energía real en lugar de
            fatiga crónica.
          </p>
        </div>
      </div>
    </motion.section>;
}
function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const faqs = [{
    question: '¿Es un libro físico?',
    answer: 'No, es un producto digital de acceso inmediato para que puedas empezar a usarlo esta misma noche.'
  }, {
    question: '¿Cuánto tiempo toma ver resultados?',
    answer: 'La mayoría de los usuarios reportan una reducción del tiempo para quedarse dormidos desde la primera sesión.'
  }, {
    question: '¿Tengo que comprar suplementos adicionales?',
    answer: 'No. Este es un método basado en técnicas cognitivas y físicas que no requieren gastos extra.'
  }, {
    question: '¿Qué pasa si me despierto a mitad de la noche?',
    answer: 'El protocolo incluye una sección de "Emergencia 3 AM" diseñada específicamente para volver a dormir en minutos si sufres un despertar nocturno.'
  }];
  return <motion.section {...fadeInUp} className="py-20 px-6 bg-[#111827]" data-id="element-90">
      <div className="max-w-3xl mx-auto" data-id="element-91">
        <h3 className="font-serif text-4xl md:text-5xl mb-12 text-center text-white" data-id="element-92">
          Preguntas Frecuentes
        </h3>
        <div className="space-y-4" data-id="element-93">
          {faqs.map((faq, index) => <div key={index} className="bg-[#0a0e1a] rounded-lg border border-gray-800 overflow-hidden" data-id="element-94">
              <button onClick={() => setOpenIndex(openIndex === index ? null : index)} className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-900/50 transition-colors" data-id="element-95">
                <span className="text-lg font-semibold text-white" data-id="element-96">
                  {faq.question}
                </span>
                <motion.div animate={{
              rotate: openIndex === index ? 180 : 0
            }} transition={{
              duration: 0.3
            }} data-id="element-97">
                  <ChevronDownIcon className="w-6 h-6 text-amber-500" data-id="element-98" />
                </motion.div>
              </button>
              <motion.div initial={false} animate={{
            height: openIndex === index ? 'auto' : 0,
            opacity: openIndex === index ? 1 : 0
          }} transition={{
            duration: 0.3
          }} className="overflow-hidden" data-id="element-99">
                <div className="px-6 pb-5 text-gray-400" data-id="element-100">{faq.answer}</div>
              </motion.div>
            </div>)}
        </div>
      </div>
    </motion.section>;
}
function OfferSection() {
  return <motion.section id="offer" {...fadeInUp} className="py-20 px-6 bg-[#0a0e1a]" data-id="element-101">
      <div className="max-w-2xl mx-auto text-center" data-id="element-102">
        <h3 className="font-serif text-3xl md:text-4xl mb-8 text-white" data-id="element-103">
          Recupera tus noches y tu energía por menos de lo que cuesta una cena
          fuera de casa.
        </h3>
        <p className="text-xl text-gray-300 mb-12" data-id="element-104">
          Invierte en tu descanso hoy y rompe el ciclo del agotamiento.
        </p>

        <div className="bg-[#111827] p-10 rounded-lg border-2 border-amber-500 mb-8" data-id="element-105">
          <div className="mb-6" data-id="element-106">
            <span className="text-2xl text-gray-500 line-through" data-id="element-107">$27 USD</span>
          </div>
          <div className="text-6xl font-bold text-amber-500 mb-8" data-id="element-108">$6.39</div>

          <motion.a href="https://bibliotecadigitalventas.com/cart/45514845487206:1" target="_blank" rel="noopener noreferrer" onClick={() => {
          if (window.fbq) {
            window.fbq('track', 'AddToCart', {
              content_name: 'Apaga Tu Mente en 15 Minutos',
              content_type: 'product',
              value: 6.39,
              currency: 'USD'
            });
          }
        }} whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} className="block bg-amber-500 hover:bg-amber-600 text-gray-900 font-bold text-xl px-16 py-6 rounded-lg shadow-2xl transition-all w-full mb-8 text-center cursor-pointer" data-id="element-109">
            ¡QUIERO DORMIR BIEN HOY MISMO!
          </motion.a>

          <div className="flex items-center justify-center gap-8 text-sm text-gray-400" data-id="element-110">
            <div className="flex items-center gap-2" data-id="element-111">
              <ZapIcon className="w-5 h-5 text-amber-500" data-id="element-112" />
              <span data-id="element-113">Acceso inmediato</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-114">
              <ShieldIcon className="w-5 h-5 text-amber-500" data-id="element-115" />
              <span data-id="element-116">Pago seguro y cifrado</span>
            </div>
          </div>
        </div>
      </div>
    </motion.section>;
}
function Footer() {
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | null>(null);
  return <>
      <footer className="py-12 px-6 bg-[#111827] border-t border-gray-800" data-id="element-117">
        <div className="max-w-4xl mx-auto text-center text-gray-500 text-sm" data-id="element-118">
          <p className="mb-4" data-id="element-119">
            © 2024 Apaga Tu Mente en 15 Minutos™. Todos los derechos
            reservados.
          </p>
          <div className="flex items-center justify-center gap-6 mb-4" data-id="element-120">
            <button onClick={() => setActiveModal('privacy')} className="hover:text-amber-500 transition-colors underline underline-offset-2" data-id="element-121">
              Política de Privacidad
            </button>
            <span className="text-gray-700" data-id="element-122">|</span>
            <button onClick={() => setActiveModal('terms')} className="hover:text-amber-500 transition-colors underline underline-offset-2" data-id="element-123">
              Términos y Condiciones
            </button>
          </div>
          <p className="text-xs" data-id="element-124">
            Este producto es una guía educativa y no sustituye el consejo médico
            profesional. Si sufres de insomnio severo o condiciones médicas
            relacionadas, consulta con un profesional de la salud.
          </p>
        </div>
      </footer>

      {/* Modal Overlay */}
      {activeModal && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-125">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} className="bg-[#111827] border border-gray-700 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-126">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700" data-id="element-127">
              <h3 className="text-xl font-semibold text-white" data-id="element-128">
                {activeModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
              </h3>
              <button onClick={() => setActiveModal(null)} className="text-gray-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-gray-800" data-id="element-129">
                <XIcon className="w-5 h-5" data-id="element-130" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-6 overflow-y-auto max-h-[calc(80vh-64px)] text-gray-300 text-sm leading-relaxed space-y-4" data-id="element-131">
              {activeModal === 'privacy' ? <>
                  <p className="text-gray-400 text-xs" data-id="element-132">
                    Última actualización: Enero 2024
                  </p>
                  <h4 className="text-white font-semibold text-base" data-id="element-133">
                    1. Información que Recopilamos
                  </h4>
                  <p data-id="element-134">
                    Recopilamos únicamente la información necesaria para
                    procesar tu compra y entregarte el producto digital,
                    incluyendo tu nombre y dirección de correo electrónico.
                  </p>
                  <h4 className="text-white font-semibold text-base" data-id="element-135">
                    2. Uso de la Información
                  </h4>
                  <p data-id="element-136">
                    Tu información se utiliza exclusivamente para: procesar tu
                    pedido, enviarte el acceso al producto digital y comunicarte
                    actualizaciones importantes relacionadas con tu compra.
                  </p>
                  <h4 className="text-white font-semibold text-base" data-id="element-137">
                    3. Protección de Datos
                  </h4>
                  <p data-id="element-138">
                    Implementamos medidas de seguridad estándar de la industria
                    para proteger tu información personal. No almacenamos datos
                    de tarjetas de crédito; todos los pagos son procesados por
                    plataformas de pago seguras y cifradas.
                  </p>
                  <h4 className="text-white font-semibold text-base" data-id="element-139">
                    4. Compartir Información
                  </h4>
                  <p data-id="element-140">
                    No vendemos, intercambiamos ni transferimos tu información
                    personal a terceros. La única excepción son los proveedores
                    de servicios de pago necesarios para completar tu
                    transacción.
                  </p>
                  <h4 className="text-white font-semibold text-base" data-id="element-141">
                    5. Cookies
                  </h4>
                  <p data-id="element-142">
                    Utilizamos cookies esenciales para el funcionamiento del
                    sitio web y cookies analíticas para mejorar tu experiencia
                    de navegación.
                  </p>
                  <h4 className="text-white font-semibold text-base" data-id="element-143">
                    6. Tus Derechos
                  </h4>
                  <p data-id="element-144">
                    Tienes derecho a acceder, rectificar o eliminar tus datos
                    personales en cualquier momento. También puedes solicitar la
                    cancelación de comunicaciones por correo electrónico.
                  </p>
                </> : <>
                  <p className="text-gray-400 text-xs" data-id="element-145">
                    Última actualización: Enero 2024
                  </p>
                  <h4 className="text-white font-semibold text-base" data-id="element-146">
                    1. Aceptación de los Términos
                  </h4>
                  <p data-id="element-147">
                    Al adquirir "Apaga Tu Mente en 15 Minutos", aceptas estos
                    términos y condiciones en su totalidad. Si no estás de
                    acuerdo, por favor no realices la compra.
                  </p>
                  <h4 className="text-white font-semibold text-base" data-id="element-148">
                    2. Naturaleza del Producto
                  </h4>
                  <p data-id="element-149">
                    Este es un producto digital educativo. No constituye consejo
                    médico, psicológico ni profesional de salud. Los resultados
                    pueden variar según cada persona.
                  </p>
                  <h4 className="text-white font-semibold text-base" data-id="element-150">
                    3. Entrega del Producto
                  </h4>
                  <p data-id="element-151">
                    El acceso al producto digital se entrega de forma inmediata
                    tras la confirmación del pago. Recibirás las instrucciones
                    de acceso en tu correo electrónico.
                  </p>
                  <h4 className="text-white font-semibold text-base" data-id="element-152">
                    4. Propiedad Intelectual
                  </h4>
                  <p data-id="element-153">
                    Todo el contenido del producto está protegido por derechos
                    de autor. No está permitida la reproducción, distribución o
                    reventa del material sin autorización expresa.
                  </p>
                  <h4 className="text-white font-semibold text-base" data-id="element-154">
                    5. Limitación de Responsabilidad
                  </h4>
                  <p data-id="element-155">
                    Este producto es una guía educativa y no garantiza
                    resultados específicos. No sustituye el tratamiento médico
                    profesional para condiciones de salud diagnosticadas.
                  </p>
                  <h4 className="text-white font-semibold text-base" data-id="element-156">
                    6. Política de Reembolso
                  </h4>
                  <p data-id="element-157">
                    Debido a la naturaleza digital e inmediata del producto,
                    todas las ventas son finales. Si experimentas problemas
                    técnicos con el acceso, nos comprometemos a resolverlos.
                  </p>
                </>}
            </div>
          </motion.div>
        </motion.div>}
    </>;
}